import { apiClient } from '@/shared/api';

import type {
  CustomerAccountSummary,
  CustomerAddress,
  CustomerConsent,
  CustomerContact,
  CustomerRecipient,
  CustomerSearchRequest,
  CustomerSearchResponse,
  CustomerSummary,
} from '../model/types';

/**
 * Customer-base CRM over the merchant-portal BFF (`/api/v1/customers/**`, tag *Customers*).
 * The BFF delegates to web-customer-service's back-office API and scopes everything to the
 * session tenant. The list endpoint takes query params; repeatable filters serialize as
 * `status=A&status=B` (no bracket indexes).
 */
const BASE = '/api/v1/customers';

/** Assembles the `GET /api/v1/customers` query from the structured search request. */
function toSearchParams(request: CustomerSearchRequest): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  const query = request.query?.trim();
  if (query) {
    params.q = query;
  }
  const criteria = request.criteria;
  if (criteria?.statuses?.length) {
    params.status = criteria.statuses;
  }
  if (criteria?.profileStatuses?.length) {
    params.profileStatus = criteria.profileStatuses;
  }
  if (criteria?.registrationChannels?.length) {
    params.channel = criteria.registrationChannels;
  }
  if (typeof criteria?.deleted === 'boolean') {
    params.deleted = criteria.deleted;
  }
  if (request.pagination?.page != null) {
    params.page = request.pagination.page;
  }
  if (request.pagination?.size != null) {
    params.size = request.pagination.size;
  }
  if (request.sort?.length) {
    params.sort = request.sort.map((option) => `${option.property},${option.direction}`);
  }
  return params;
}

export async function searchCustomers(request: CustomerSearchRequest): Promise<CustomerSearchResponse> {
  return (
    await apiClient.get<CustomerSearchResponse>(BASE, {
      params: toSearchParams(request),
      paramsSerializer: { indexes: null },
    })
  ).data;
}

export async function getCustomerSummary(customerId: string): Promise<CustomerSummary> {
  return (await apiClient.get<CustomerSummary>(`${BASE}/${customerId}`)).data;
}

export async function getCustomerContacts(customerId: string): Promise<CustomerContact[]> {
  return (await apiClient.get<CustomerContact[]>(`${BASE}/${customerId}/contacts`)).data;
}

export async function getCustomerAddresses(customerId: string): Promise<CustomerAddress[]> {
  return (await apiClient.get<{ addresses: CustomerAddress[] }>(`${BASE}/${customerId}/addresses`)).data.addresses ?? [];
}

export async function getCustomerRecipients(customerId: string): Promise<CustomerRecipient[]> {
  return (
    (await apiClient.get<{ recipients: CustomerRecipient[] }>(`${BASE}/${customerId}/recipients`)).data.recipients ?? []
  );
}

export async function getCustomerConsents(customerId: string): Promise<CustomerConsent[]> {
  return (await apiClient.get<CustomerConsent[]>(`${BASE}/${customerId}/consents`)).data;
}

export async function getCustomerAccounts(customerId: string): Promise<CustomerAccountSummary[]> {
  return (await apiClient.get<CustomerAccountSummary[]>(`${BASE}/${customerId}/accounts`)).data;
}
